const SS_ICONS = new Proxy({}, { get: (_, name) => `<span class="ss-i ss-i-${name}" aria-hidden="true"></span>` });

class StrawberryMenuSystem extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.data = this.readData();
    this.config = this.data.config || {};
    this.menu = this.data.menu || [];
    this.proofs = this.data.proofs || [];
    this.promotions = this.data.promotions || [];
    this.active = this.menu[0]?.id || '';
    this.render();
    this.bind();
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  readData() {
    try { return JSON.parse(this.querySelector('[data-ss-menu-data]')?.textContent || '{}'); }
    catch (error) { console.error('Strawberry menu data is invalid', error); return {}; }
  }

  escape(value = '') {
    const node = document.createElement('div');
    node.textContent = String(value);
    return node.innerHTML;
  }

  safeUrl(value = '#') {
    const url = String(value || '#').trim();
    return /^(https?:|mailto:|tel:|\/|#)/i.test(url) ? this.escape(url) : '#';
  }

  render() {
    const c = this.config;
    const utilityLinks = (c.utilityLinks || [])
      .filter(link => String(link.label || '').trim().toLowerCase() !== 'events')
      .map(link => String(link.label || '').trim().toLowerCase() === 'contact' ? { ...link, label: 'Contact Us' } : link);
    this.innerHTML = `
      <header class="ss-menu ${c.sticky ? 'ss-menu--sticky' : ''} ${c.allowLogoOverflow ? 'ss-menu--logo-overflow' : ''}" data-open="false" data-mobile-open="false" style="--ss-panel-columns:${Number(c.panelColumns) || 4}">
        ${c.showUtility ? `<div class="ss-menu__utility"><span>${this.escape(c.utilityText)}</span><nav class="ss-menu__utility-links" aria-label="Utility navigation">${utilityLinks.map(link => `<a href="${this.safeUrl(link.url)}">${this.escape(link.label)}</a>`).join('')}</nav></div>` : ''}
        <div class="ss-menu__bar">
          <a class="ss-menu__logo" href="${this.safeUrl(c.homeUrl || '/')}" aria-label="${this.escape(c.brand || 'Store')} home">
            ${c.logo ? `<img class="ss-menu__logo-image" src="${this.safeUrl(c.logo)}" alt="${this.escape(c.brand || '')}" style="width:${Number(c.logoWidth) || 230}px">` : `<span class="ss-menu__berry">${SS_ICONS.berry}</span><span class="ss-menu__brand"><span class="ss-menu__brand-title">${this.escape(c.brand)}</span>${c.subtitle ? `<span class="ss-menu__brand-subtitle">${this.escape(c.subtitle)}</span>` : ''}</span>`}
          </a>
          ${c.showSearch ? `<form class="ss-menu__search" action="/search" method="get" role="search"><label class="sr-only" for="ss-search-${this.dataset.sectionId}">Search</label><input id="ss-search-${this.dataset.sectionId}" type="search" name="q" placeholder="${this.escape(c.searchPlaceholder)}"><button type="submit" aria-label="Search">${SS_ICONS.search}</button></form>` : '<span></span>'}
          <div class="ss-menu__actions">
            ${c.showAccount ? `<a href="/account" class="ss-menu__action">${SS_ICONS.user}<span><b>My account</b><span>Sign in / Register</span></span></a>` : ''}
            ${c.showWishlist && c.wishlistUrl ? `<a href="${this.safeUrl(c.wishlistUrl)}" class="ss-menu__action ss-menu__saved-action" data-saved-ideas-link>${SS_ICONS.heart}<span><b>Saved Ideas <em data-saved-ideas-count hidden>0</em></b><span>Favorites</span></span></a>` : ''}
            ${c.showCart ? `<a href="/cart" class="ss-menu__action">${SS_ICONS.bag}<span><b>Cart</b><span>${Number(c.cartCount) || 0} item${Number(c.cartCount) === 1 ? '' : 's'}</span></span></a>` : ''}
          </div>
          <button class="ss-menu__mobile-toggle" type="button" aria-expanded="false" aria-controls="ss-drawer-${this.dataset.sectionId}" aria-label="Open menu">${SS_ICONS.menu}</button>
        </div>
        <div class="ss-menu__nav-wrap"><nav class="ss-menu__nav" aria-label="Main navigation">${this.menu.map(item => this.navItem(item)).join('')}</nav></div>
        <div class="ss-menu__panel" aria-hidden="true">${this.panelTemplate()}</div>
        ${c.showOverlay ? '<button class="ss-menu__overlay" type="button" aria-label="Close menu"></button>' : ''}
        <aside id="ss-drawer-${this.dataset.sectionId}" class="ss-menu__mobile-drawer" aria-label="Mobile navigation" aria-hidden="true">
          <div class="ss-menu__mobile-head"><strong>${this.escape(c.brand || 'Menu')}</strong><button type="button" class="ss-menu__mobile-close" aria-label="Close menu">${SS_ICONS.close}</button></div>
          ${c.showSearch ? `<form class="ss-menu__mobile-search" action="/search" method="get"><input type="search" name="q" placeholder="${this.escape(c.searchPlaceholder)}"><button type="submit" aria-label="Search">${SS_ICONS.search}</button></form>` : ''}
          <div class="ss-menu__mobile-items">${this.menu.map((item, index) => this.mobileItem(item, index)).join('')}</div>
          <nav class="ss-menu__mobile-utility" aria-label="Account links">${c.showAccount ? '<a href="/account">My account</a>' : ''}${c.showWishlist && c.wishlistUrl ? `<a href="${this.safeUrl(c.wishlistUrl)}" data-saved-ideas-link>Saved Ideas <span data-saved-ideas-count hidden>0</span></a>` : ''}${c.showCart ? `<a href="/cart">Cart (${Number(c.cartCount) || 0})</a>` : ''}${utilityLinks.map(link => `<a href="${this.safeUrl(link.url)}">${this.escape(link.label)}</a>`).join('')}</nav>
        </aside>
      </header>`;
  }

  navItem(item) {
    const expandable = item.links?.length || item.promo?.title || item.promo?.image;
    const badge = item.badge ? `<span class="ss-menu__badge">${this.escape(item.badge)}</span>` : '';
    if (!expandable && item.url && item.url !== '#') return `<a class="ss-menu__nav-button ${item.accent ? 'ss-menu__accent' : ''}" href="${this.safeUrl(item.url)}">${this.escape(item.label)}${badge}</a>`;
    return `<button class="ss-menu__nav-button ${item.accent ? 'ss-menu__accent' : ''}" type="button" data-menu-id="${this.escape(item.id)}" aria-expanded="false" aria-haspopup="true">${this.escape(item.label)}${badge}</button>`;
  }

  panelTemplate() {
    const item = this.menu.find(entry => entry.id === this.active) || this.menu[0];
    if (!item) return '';
    const promo = item.promo || {};
    const reusablePromos = this.promotions.filter(card => !card.targets?.length || card.targets.some(target => target.toLowerCase() === item.label.toLowerCase()));
    const promos = [...reusablePromos, ...(promo.image || promo.title ? [promo] : [])];
    return `<div class="ss-menu__panel-inner ${promos.length ? 'has-promo' : ''}">
      <div class="ss-menu__columns">
        ${item.heading ? `<div class="ss-menu__panel-heading">${this.escape(item.heading)}</div>` : ''}
        <div class="ss-menu__columns-grid">${(item.links || []).map(link => this.columnTemplate(link)).join('')}</div>
        ${this.proofs.length ? `<div class="ss-menu__proof">${this.proofs.map(proof => this.proofTemplate(proof)).join('')}</div>` : ''}
      </div>
      ${promos.length ? `<div class="ss-menu__promos">${promos.map(card => `<article class="ss-menu__promo">${card.image ? `<a href="${this.safeUrl(card.url)}"><img src="${this.safeUrl(card.image)}" alt="${this.escape(card.title)}" loading="lazy"></a>` : ''}<div class="ss-menu__promo-body">${card.title ? `<h3>${this.escape(card.title)}</h3>` : ''}${card.text ? `<p>${this.escape(card.text)}</p>` : ''}${card.url ? `<a href="${this.safeUrl(card.url)}">${this.escape(card.button || 'Shop now')} <span aria-hidden="true">→</span></a>` : ''}</div></article>`).join('')}</div>` : ''}
    </div>`;
  }

  columnTemplate(link) {
    const children = link.children || [];
    return `<section class="ss-menu__column"><h3><a href="${this.safeUrl(link.url)}">${this.escape(link.label)}</a></h3><ul>${children.map(child => `<li><a href="${this.safeUrl(child.url)}">${this.escape(child.label)}</a>${child.children?.length ? `<ul class="ss-menu__third-level">${child.children.map(grand => `<li><a href="${this.safeUrl(grand.url)}">${this.escape(grand.label)}</a></li>`).join('')}</ul>` : ''}</li>`).join('')}</ul></section>`;
  }

  proofTemplate(proof) {
    const content = `${SS_ICONS[proof.icon || 'heart']}<span><strong>${this.escape(proof.title)}</strong><span>${this.escape(proof.text)}</span></span>`;
    return proof.url ? `<a class="ss-menu__proof-item" href="${this.safeUrl(proof.url)}">${content}</a>` : `<div class="ss-menu__proof-item">${content}</div>`;
  }

  mobileItem(item, index) {
    const hasChildren = item.links?.length;
    if (!hasChildren) return `<a class="ss-menu__mobile-direct ${item.accent ? 'ss-menu__accent' : ''}" href="${this.safeUrl(item.url)}">${this.escape(item.label)}</a>`;
    return `<details class="ss-menu__mobile-section" ${this.config.mobileOpenFirst && index === 0 ? 'open' : ''}><summary>${this.escape(item.label)}${item.badge ? `<span class="ss-menu__badge">${this.escape(item.badge)}</span>` : ''}<span class="ss-menu__plus" aria-hidden="true">+</span></summary><div class="ss-menu__mobile-links">${item.url && item.url !== '#' ? `<a class="ss-menu__view-all" href="${this.safeUrl(item.url)}">View all ${this.escape(item.label)}</a>` : ''}${item.links.map(link => `<div class="ss-menu__mobile-group"><a class="ss-menu__mobile-heading" href="${this.safeUrl(link.url)}">${this.escape(link.label)}</a>${(link.children || []).map(child => `<a href="${this.safeUrl(child.url)}">${this.escape(child.label)}</a>`).join('')}</div>`).join('')}</div></details>`;
  }

  bind() {
    this.abort = new AbortController();
    const signal = this.abort.signal;
    const root = this.querySelector('.ss-menu');
    const panel = this.querySelector('.ss-menu__panel');
    const navWrap = this.querySelector('.ss-menu__nav-wrap');
    this.querySelectorAll('.ss-menu__search, .ss-menu__mobile-search').forEach(form => {
      form.addEventListener('submit', event => {
        const input = form.querySelector('input[name="q"]');
        if (input?.value.trim()) return;
        event.preventDefault();
        input?.blur();
        this.closePanel();
      }, { signal });
    });
    this.querySelectorAll('[data-menu-id]').forEach(button => {
      if (this.config.openMode !== 'click') button.addEventListener('mouseenter', () => this.activate(button.dataset.menuId), { signal });
      button.addEventListener('focus', () => this.activate(button.dataset.menuId), { signal });
      button.addEventListener('click', () => root.dataset.open === 'true' && this.active === button.dataset.menuId ? this.closePanel() : this.activate(button.dataset.menuId), { signal });
    });
    root.addEventListener('mouseleave', () => this.closePanel(), { signal });
    navWrap?.addEventListener('mouseleave', event => {
      if (event.clientY <= navWrap.getBoundingClientRect().top + 1) this.closePanel();
    }, { signal });
    this.querySelector('.ss-menu__bar')?.addEventListener('mouseenter', () => this.closePanel(), { signal });
    this.querySelector('.ss-menu__utility')?.addEventListener('mouseenter', () => this.closePanel(), { signal });
    document.addEventListener('mousemove', event => {
      if (root.dataset.open === 'true' && event.clientY < navWrap.getBoundingClientRect().top) this.closePanel();
    }, { signal });
    panel.addEventListener('click', event => { if (event.target.closest('a')) this.closePanel(); }, { signal });
    this.querySelector('.ss-menu__mobile-toggle')?.addEventListener('click', () => this.setMobile(true), { signal });
    this.querySelector('.ss-menu__mobile-close')?.addEventListener('click', () => this.setMobile(false), { signal });
    this.querySelector('.ss-menu__overlay')?.addEventListener('mouseenter', () => this.closePanel(), { signal });
    this.querySelector('.ss-menu__overlay')?.addEventListener('click', () => { this.closePanel(); this.setMobile(false); }, { signal });
    document.addEventListener('click', event => { if (!this.contains(event.target)) this.closePanel(); }, { signal });
    document.addEventListener('keydown', event => { if (event.key === 'Escape') { this.closePanel(); this.setMobile(false); } }, { signal });
  }

  activate(id) {
    this.active = id;
    const root = this.querySelector('.ss-menu');
    root.dataset.open = 'true';
    this.querySelector('.ss-menu__panel').innerHTML = this.panelTemplate();
    this.querySelector('.ss-menu__panel').setAttribute('aria-hidden', 'false');
    this.querySelectorAll('[data-menu-id]').forEach(button => button.setAttribute('aria-expanded', String(button.dataset.menuId === id)));
  }

  closePanel() {
    const root = this.querySelector('.ss-menu');
    if (!root) return;
    root.dataset.open = 'false';
    this.querySelector('.ss-menu__panel')?.setAttribute('aria-hidden', 'true');
    this.querySelectorAll('[data-menu-id]').forEach(button => button.setAttribute('aria-expanded', 'false'));
  }

  setMobile(open) {
    const root = this.querySelector('.ss-menu');
    root.dataset.mobileOpen = String(open);
    root.querySelector('.ss-menu__mobile-drawer')?.setAttribute('aria-hidden', String(!open));
    root.querySelector('.ss-menu__mobile-toggle')?.setAttribute('aria-expanded', String(open));
    document.documentElement.classList.toggle('ss-menu-lock', open);
  }
}

if (!customElements.get('strawberry-menu-system')) customElements.define('strawberry-menu-system', StrawberryMenuSystem);
